import {
  Container,
  Stack,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  Heading,
  Wrap,
  Badge,
  Button,
  HStack,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import t from "../translations";

const LEARNING_TABS = [
  {
    label: "Front-End",
    color: "yellow",
    description: t.frontEndLanding.description,
    topics: t.frontEndTopics,
    basePath: "/frontEndLearning",
    overflowSuffix: t.frontEndLanding.badgeOverflowSuffix,
  },
  {
    label: "Back-End",
    color: "red",
    description: t.backEndLanding.description,
    topics: t.backEndTopics,
    basePath: "/backEndLearning",
    overflowSuffix: t.backEndLanding.badgeOverflowSuffix,
  },
  {
    label: "Testing & QA",
    color: "cyan",
    description: t.testingLanding.description,
    topics: t.testingTopics,
    basePath: "/testingLearning",
    overflowSuffix: t.testingLanding.badgeOverflowSuffix,
  },
  {
    label: "Mobile Dev",
    color: "pink",
    description: t.mobileLanding.description,
    topics: t.mobileTopics,
    basePath: "/mobileLearning",
    overflowSuffix: t.mobileLanding.badgeOverflowSuffix,
  },
  {
    label: "Software Design",
    color: "orange",
    description: t.designLanding.description,
    topics: t.designTopics,
    basePath: "/designLearning",
    overflowSuffix: t.designLanding.badgeOverflowSuffix,
  },
];

function TopicCard({ item, basePath, color, index, overflowSuffix }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.08 });
  const navigate = useNavigate();
  const cardBg = useColorModeValue("white", "gray.800");

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.45, delay: index * 0.08, ease: "easeOut" }}
      whileHover={{ y: -6 }}
      style={{ height: "100%" }}
    >
      <Card
        bg={cardBg}
        h="100%"
        cursor="pointer"
        onClick={() => navigate(`${basePath}/${item.slug}`)}
        _hover={{
          boxShadow: `0 0 28px 6px var(--chakra-colors-${color}-400)`,
        }}
        transition="box-shadow 0.3s ease"
        borderRadius="xl"
      >
        <CardBody>
          <Stack spacing={4} h="100%" justify="space-between">
            <Stack spacing={3}>
              <HStack justify="space-between">
                <Badge
                  colorScheme={color}
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontSize="xs"
                >
                  {item.duration}
                </Badge>
              </HStack>
              <Heading size="md" textAlign="left">
                {item.company}
              </Heading>
              <Text color="gray.500" fontSize="sm" textAlign="left">
                {item.position}
              </Text>
              <Wrap spacing={1}>
                {item.badges.slice(0, 4).map((badge) => (
                  <Badge
                    key={badge.name}
                    colorScheme={badge.colorScheme}
                    fontSize="xs"
                  >
                    {badge.name}
                  </Badge>
                ))}
                {item.badges.length > 4 && (
                  <Badge colorScheme="gray" fontSize="xs">
                    +{item.badges.length - 4} {overflowSuffix}
                  </Badge>
                )}
              </Wrap>
            </Stack>
            <Button
              size="sm"
              colorScheme={color}
              variant="ghost"
              rightIcon={<ArrowForwardIcon />}
              alignSelf="flex-start"
              pointerEvents="none"
            >
              View topic
            </Button>
          </Stack>
        </CardBody>
      </Card>
    </motion.div>
  );
}

export default function LearningPage({ color }) {
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(location.state?.tabIndex ?? 0);
  const tabBg = useColorModeValue("gray.100", "gray.700");
  const selectedBg = useColorModeValue("white", "gray.800");

  useEffect(() => {
    if (location.state?.tabIndex != null) {
      setTabIndex(location.state.tabIndex);
    }
  }, [location.state]);

  useEffect(() => {
    document.title = "Learning | The Coding Explorer";
  }, []);

  return (
    <Container maxW="5xl" pt={{ base: 20, md: 28 }} pb={{ base: 20, md: 36 }}>
      <Stack spacing={{ base: 8, md: 10 }}>
        <Stack align="center" direction="row" px={4}>
          <HStack mx={4}>
            <Text fontWeight={800} fontSize={{ base: "xl", md: "2xl" }} whiteSpace="nowrap">
              Learning
            </Text>
          </HStack>
          <Divider />
        </Stack>

        <Tabs
          variant="unstyled"
          colorScheme={color}
          isLazy
          index={tabIndex}
          onChange={setTabIndex}
        >
          <TabList
            justifyContent="center"
            gap={2}
            px={{ base: 2, md: 4 }}
            flexWrap="wrap"
          >
            {LEARNING_TABS.map((tab) => (
              <Tab
                key={tab.label}
                fontWeight={600}
                fontSize={{ base: "xs", md: "sm" }}
                px={{ base: 3, md: 6 }}
                py={2}
                borderRadius="full"
                bg={tabBg}
                color="gray.500"
                _selected={{
                  bg: selectedBg,
                  color: `${tab.color}.400`,
                  boxShadow: `0 0 14px 2px var(--chakra-colors-${tab.color}-400)`,
                }}
                transition="all 0.2s"
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          <TabPanels mt={8}>
            {LEARNING_TABS.map((tab) => (
              <TabPanel key={tab.label} px={0}>
                <Stack spacing={{ base: 6, md: 8 }}>
                  <Text
                    color="gray.500"
                    px={4}
                    textAlign="center"
                    fontSize="md"
                  >
                    {tab.description}
                  </Text>

                  <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3 }}
                    spacing={6}
                    px={4}
                  >
                    {tab.topics.map((item, i) => (
                      <TopicCard
                        key={item.slug}
                        item={item}
                        basePath={tab.basePath}
                        color={tab.color}
                        index={i}
                        overflowSuffix={tab.overflowSuffix}
                      />
                    ))}
                  </SimpleGrid>
                </Stack>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Stack>
    </Container>
  );
}
